import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme } from '@datatype/type';
import TextBlock from '@component/common/block/text-block';
import TextButton from '@design-system/text-button';
import Gap from '@component/common/gap';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import { useClassroomGrammarWorkbooksQuery } from '@hook/iyagigo/useApi';
import { groupClassroomGrammars } from '@util/classroom';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as classroomActions } from '@store/slice/classroom-slice';
import { v4 as uuid4 } from 'uuid';

// ###################################
// GrammarStageSelectModal — 기초문법 단계 선택 (study-configuration 옵션 선택 패턴)
// - 옵션 목록은 classroom grammar API 응답을 단계별로 묶어 사용
// - 옵션 클릭 → 로컬 선택(하이라이트)만, 하단 '학습하기' 버튼으로 확정(slice 저장 + 닫기)
// - 옵션 텍스트는 리스트형이라 왼쪽 정렬 (DesignOption은 가운데 고정이라 전용 스타일 사용)
// ###################################

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: ${size.MEDIUM_MODAL_WIDTH};
`;

const OptionContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 5px;
  width: 100%;
`;

// DesignOption 선택 테두리 로직(선택 BROWN / 기본 GRAY) 유지 + 텍스트 왼쪽 정렬
const OptionItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 12px;
  border-radius: ${size.BORDER_RADIUS_2};
  border: ${size.BORDER_WIDTH_1} solid ${({ $selected }) => ($selected ? color.BROWN_1 : color.GRAY_BOX_BORDER_COLOR)};
  cursor: pointer;
  gap: 10px;
`;

const GrammarStageSelectModal: React.FC = () => {
  const dispatch = useTypedDispatch();
  const languageCode =
    useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? EApiLanguageCode.JAPANESE;
  const storedStageKey =
    useTypedSelector((state) => state.rootReducer?.classroomReducer?.currentGrammarStageKey) ?? null;
  const { data: workbooksResponse } = useClassroomGrammarWorkbooksQuery(languageCode);
  const stages = groupClassroomGrammars((workbooksResponse?.data ?? []).map((workbook) => workbook.classroomGrammar));
  const currentStageKey = stages.some((stage) => stage.key === storedStageKey) ? storedStageKey : stages[0]?.key;

  // 로컬 선택 상태(초기값=현재 단계). '학습하기' 눌러야 확정.
  const [selectedKey, setSelectedKey] = useState<string>(currentStageKey ?? '');

  useEffect(() => {
    if (!selectedKey && currentStageKey) setSelectedKey(currentStageKey);
  }, [currentStageKey, selectedKey]);

  const handleConfirm = () => {
    if (!selectedKey) return;
    dispatch(classroomActions.storeCurrentGrammarStageKey(selectedKey));
    dispatch(modalActions.hideModal());
  };

  const Options = stages.map((stage) => (
    <OptionItem key={uuid4()} $selected={stage.key === selectedKey} onClick={() => setSelectedKey(stage.key)}>
      {/* 'N단계'는 order로 조합 (title엔 순수 주제명만 저장됨) */}
      <TextBlock
        text={`<strong>${stage.order + 1}단계</strong>`}
        design={{ text: { size: 'small', color: color.BLACK_4 }, box: { alignX: 'left' } }}
      />
      <TextBlock
        text={stage.name?.ko ?? ''}
        design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'left' } }}
      />
    </OptionItem>
  ));

  return (
    <Wrapper>
      <TextBlock
        text='학습할 단계를 선택해주세요'
        design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'center' } }}
      />
      <Gap height={size.MARGIN_1} />
      <OptionContainer>{Options}</OptionContainer>
      <Gap height={size.MARGIN_2} />
      <TextButton
        text='학습하기'
        theme={ETheme.BROWN}
        size='medium'
        shape='pill'
        width='100%'
        handleClick={handleConfirm}
      />
    </Wrapper>
  );
};

export default GrammarStageSelectModal;
